import React from 'react';
import { parseDesc, listaRequerimientos } from '../hooks/useEventos';
import { fmtHora, fmtFechaCorta, val, normISO } from '../utils/surgeryHelpers';

const STRIPE = {
  pending   : '#04afaa',
  confirmada: '#d97706',
  cancelada : '#e12f3b',
  historico : '#9ca3af',
};

const BADGE = {
  pending   : { bg: '#e6f9f8', color: '#038f8a', label: 'Pendiente'  },
  confirmada: { bg: '#fffbeb', color: '#d97706', label: 'Confirmada' },
  cancelada : { bg: '#fdf1f2', color: '#e12f3b', label: 'Cancelada'  },
  historico : { bg: '#f3f4f6', color: '#6b7280', label: 'Realizado'  },
};

const SurgeryCard = ({ doc, tipo, onConfirmar, onCancelar, onEditar, onDetalle, onTiempos, mostrarUnidad }) => {
  // Todos los campos están dentro de doc.event
  const ev       = doc?.event || {};
  const summary  = (ev.summary || '').toLowerCase();
  const startIso = ev.start?.dateTime;
  const endIso   = ev.end?.dateTime;
  const fields   = parseDesc(ev.description);

  const esCancelado = summary === 'cancelada';
  const key    = esCancelado ? 'cancelada' : (tipo === 'historico' ? 'historico' : summary || 'pending');
  const badge  = BADGE[key]  || BADGE.pending;
  const stripe = STRIPE[key] || STRIPE.pending;

  // El botón "Realizada" solo aplica una vez que ya pasó la hora de inicio programada
  const yaInicio = startIso && new Date(normISO(startIso)) <= new Date();

  const quirofano    = val(fields['AppointmentType']) || 'Quirófano';
  const unidad       = val(fields['Unidad Hospitalaroa']) || val(fields['Unidad Hospitalaria']);
  const medico       = val(fields['Medico']);
  const paciente     = val(fields['Paciente']);
  const procedimiento= val(fields['Procedimiento']);
  const telefono     = val(fields['Telefono Medico']);
  const duracion     = val(fields['Duracion']);
  const tipoCliente  = val(fields['TipoCliente']);
  const reqs         = listaRequerimientos(doc);

  return (
    <div
      onClick={() => onDetalle && onDetalle(doc)}
      role={onDetalle ? 'button' : undefined}
      tabIndex={onDetalle ? 0 : undefined}
      onKeyDown={(e) => {
        if (onDetalle && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onDetalle(doc); }
      }}
      onMouseEnter={(e) => { if (onDetalle) e.currentTarget.style.boxShadow = '0 4px 14px rgba(0,0,0,.12)'; }}
      onMouseLeave={(e) => { if (onDetalle) e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,.08)'; }}
      style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:'14px', boxShadow:'0 1px 3px rgba(0,0,0,.08)', cursor: onDetalle ? 'pointer' : 'default', display:'flex', flexDirection:'column', overflow:'hidden', transition:'box-shadow .2s,transform .15s', width:'272px' }}
    >

      <div style={{ height:'4px', background: stripe }} />

      <div style={{ padding:'14px 14px 10px', flex:1 }}>

        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'8px' }}>
          <span style={{ color:'#039490', fontSize:'14px', fontWeight:800, textTransform:'uppercase', letterSpacing:'.4px' }}>
            {quirofano}
          </span>
          <span style={{ background:badge.bg, color:badge.color, borderRadius:'20px', fontSize:'9px', fontWeight:800, letterSpacing:'.6px', padding:'3px 8px', textTransform:'uppercase', whiteSpace:'nowrap' }}>
            {badge.label}
          </span>
        </div>

        {mostrarUnidad && unidad && (
          <div style={{ alignItems:'center', color:'#7c3aed', display:'flex', fontSize:'10px', fontWeight:700, gap:'4px', marginBottom:'8px', marginTop:'-4px' }}>
            🏥 {unidad}
          </div>
        )}

        <div style={{ background:'#f9fafb', borderRadius:'8px', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px', marginBottom:'12px', padding:'8px' }}>
          <span style={{ fontSize:'17px', fontWeight:800, color:'#111827' }}>{fmtHora(startIso)}</span>
          <span style={{ color:'#9ca3af' }}>→</span>
          <span style={{ fontSize:'17px', fontWeight:800, color:'#111827' }}>{fmtHora(endIso)}</span>
          {duracion && <span style={{ fontSize:'10px', color:'#6b7280' }}>({duracion}h)</span>}
        </div>

        <Field label="Fecha"          value={fmtFechaCorta(startIso)} />
        {medico        && <Field label="Médico"         value={medico}        sub={telefono ? `☏ ${telefono}` : null} subColor="#0582a9" />}
        {paciente      && <Field label="Paciente"       value={paciente}      sub={tipoCliente} />}
        {procedimiento && <Field label="Procedimiento"  value={procedimiento} />}

        {reqs.length > 0 && (
          <div style={{ background:'#fffbeb', border:'1px solid #fcd34d', borderRadius:'8px', color:'#d97706', display:'flex', alignItems:'center', fontSize:'11px', fontWeight:600, gap:'6px', marginBottom:'10px', padding:'7px 10px' }}>
            ⚠ {reqs.join(' · ')}
          </div>
        )}

        <hr style={{ border:'none', borderTop:'1px solid #f3f4f6', margin:'8px 0' }} />
      </div>

      <div style={{ display:'flex', flexWrap:'wrap', gap:'5px', padding:'0 14px 12px' }} onClick={(e) => e.stopPropagation()}>
        {tipo === 'pendientes' && !esCancelado && onConfirmar && (
          <Btn color="#85c100" bg="#f2f9e6" onClick={() => onConfirmar(doc)}>✓ Confirmar</Btn>
        )}
        {tipo !== 'historico' && !esCancelado && onEditar && (
          <Btn color="#0582a9" bg="#e8f4f8" onClick={() => onEditar(doc)}>✎ Reagendar</Btn>
        )}
        {tipo !== 'historico' && !esCancelado && onCancelar && (
          <Btn color="#e12f3b" bg="#fdf1f2" onClick={() => onCancelar(doc)}>✕ Cancelar</Btn>
        )}
        {tipo !== 'pendientes' && !esCancelado && onTiempos && yaInicio && (
          <Btn color="#7c3aed" bg="#f3ecfe" onClick={() => onTiempos(doc)}>✓ Realizada</Btn>
        )}
        {onDetalle && (
          <Btn color="#374151" bg="#f3f4f6" onClick={() => onDetalle(doc)}>⊞ Detalles</Btn>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, value, sub, subColor, small }) => (
  <div style={{ marginBottom:'7px' }}>
    <span style={{ color:'#6b7280', display:'block', fontSize:'9px', fontWeight:700, letterSpacing:'.5px', marginBottom:'2px', textTransform:'uppercase' }}>{label}</span>
    <span style={{ color:'#111827', display:'block', fontSize: small ? '11px' : '12px', fontWeight:600, lineHeight:1.3 }}>{value}</span>
    {sub && <span style={{ color: subColor || '#6b7280', display:'block', fontSize:'11px' }}>{sub}</span>}
  </div>
);

const Btn = ({ children, color, bg, onClick }) => (
  <button onClick={onClick} style={{ alignItems:'center', background:bg, border:`1px solid ${color}`, borderRadius:'8px', color, cursor:'pointer', display:'inline-flex', fontSize:'10px', fontWeight:700, gap:'4px', padding:'5px 9px' }}>
    {children}
  </button>
);

export default SurgeryCard;